import React from "react";
import { FaCogs, FaTools, FaMicrochip } from "react-icons/fa";

const IndustrialGrid = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-slate-50 p-1 font-mono text-slate-800 min-h-[1100px] flex flex-col justify-between">
            <div className="bg-white border-2 border-slate-900 m-8 p-12 shadow-[15px_15px_0px_0px_rgba(15,23,42,1)] flex-1">
                {/* Header */}
                <div className="flex justify-between items-start border-b-4 border-slate-900 pb-10 mb-10">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 bg-slate-900 flex items-center justify-center text-white text-3xl">
                                <FaCogs />
                            </div>
                            <h1 className="text-3xl font-black uppercase tracking-tighter text-slate-900 border-b-2 border-slate-900 pb-1">{companyDetails?.name}</h1>
                        </div>
                        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest bg-slate-100 p-3 border-l-4 border-slate-900">{companyDetails?.address}</div>
                    </div>

                    <div className="text-right flex flex-col items-end">
                        <div className="bg-slate-900 text-white px-8 py-3 text-sm font-black uppercase tracking-widest mb-4">
                            DOCUMENT REF: {title}
                        </div>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                            <div>Date Issued</div>
                            <div className="text-slate-900">{date}</div>
                            <div>Term Limit</div>
                            <div className="text-rose-600 font-black">{dueDate}</div>
                        </div>
                    </div>
                </div>

                {/* Client Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-slate-100 border-b-2 border-slate-900 mb-10 pb-10">
                    <div className="pr-10">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2">Client Identity</h4>
                        <div className="text-xl font-black text-slate-900 tracking-tighter uppercase italic">{to}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">{clientAddress}</div>
                    </div>

                    <div className="px-10">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2">Contact Link</h4>
                        <div className="text-xs font-black text-slate-900 space-y-1">
                            <div>{clientEmail || "N/A"}</div>
                            <div>{clientPhone || "N/A"}</div>
                        </div>
                    </div>

                    <div className="pl-10">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2">Company Link</h4>
                        <div className="text-xs font-black text-slate-900 space-y-1 italic">
                            <div>{companyDetails?.email}</div>
                            <div>{companyDetails?.phone}</div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-10 overflow-hidden rounded-xl border border-slate-200">
                    <table className="w-full text-left bg-white">
                        <thead className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">
                            <tr>
                                <th className="p-6">Component / Description</th>
                                <th className="p-6 text-center border-l border-white/10">Unit</th>
                                <th className="p-6 text-right border-l border-white/10">Rate</th>
                                <th className="p-6 text-right border-l border-white/10 italic">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {(invoiceItems || []).map((item, idx) => (
                                <tr key={idx} className="group hover:bg-slate-50 transition-colors">
                                    <td className="p-6 border-r border-slate-50 group-hover:bg-slate-900 group-hover:text-white transition-all">
                                        <div className="text-lg font-black tracking-tighter uppercase italic">{item.item}</div>
                                        <div className="text-[9px] font-bold opacity-50 uppercase tracking-widest mt-1">Ref No: {idx + 101} — Status: Verified</div>
                                    </td>
                                    <td className="p-6 text-center text-xs font-black text-slate-400 tracking-widest leading-none">{item.qty}</td>
                                    <td className="p-6 text-right text-xs font-black text-slate-900 uppercase tracking-tighter leading-none italic">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-6 text-right text-lg font-black text-slate-900 tracking-tighter leading-none italic bg-slate-50 border-l border-slate-100 pl-8">
                                        Nu. {parseFloat(item.amount).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals Section */}
                <div className="grid grid-cols-2 gap-10">
                    <div className="space-y-6">
                        <div className="bg-slate-100 p-8 border-2 border-slate-900 shadow-[5px_5px_0px_0px_rgba(226,232,240,1)] relative overflow-hidden group">
                            <FaMicrochip className="absolute top-0 right-0 text-slate-200 text-6xl -mr-4 -mt-4 opacity-50 group-hover:rotate-12 transition-transform duration-700" />
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 items-center flex gap-2">
                                <FaTools className="text-slate-900" /> Operational Remarks / Terms
                            </h4>
                            <p className="text-[10px] font-black leading-loose text-slate-600 uppercase tracking-widest italic pr-10">
                                {notes || "Operational standards have been maintained during the service period. Product guarantee applies to manufacturing only."}
                                <br /><br />
                                {terms || "Payments must conform to the established 30-day billing cycle."}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white border-2 border-slate-900 p-8 relative overflow-hidden">
                            <div className="space-y-4 font-black uppercase tracking-widest text-[10px] italic">
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-slate-400">
                                    <span>Base Amount</span>
                                    <span>Nu. {parseFloat(subTotal).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-b border-slate-50 pb-2 text-slate-400">
                                    <span>Maintenance Tax ({taxRate}%)</span>
                                    <span>Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between border-b border-slate-50 pb-2 text-rose-500">
                                        <span>Consolidation Discount</span>
                                        <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="pt-6 mt-6 border-t-4 border-slate-900 flex justify-between items-end">
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900 underline underline-offset-4 decoration-slate-900 decoration-2 italic">Total Investment</div>
                                        <div className="text-5xl font-black text-slate-900 tracking-tighter italic leading-none pt-2">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-12 border-t-2 border-slate-900 pt-10 flex justify-between items-end">
                    <div className="bg-slate-900 text-white p-6 shadow-[8px_8px_0px_0px_rgba(203,213,225,1)]">
                        <h4 className="text-[9px] font-black uppercase tracking-[0.4em] mb-3 text-slate-400 underline underline-offset-4 decoration-slate-700 decoration-2 italic">Official Disbursement Details</h4>
                        <div className="text-xs font-black tracking-tighter space-y-1 uppercase italic leading-none">
                            <div className="whitespace-nowrap overflow-hidden text-ellipsis">{companyDetails?.bankName || "National Industrial Bank"}</div>
                            <div className="text-slate-400 tracking-[.1em] text-[10px]">ACC ID: {companyDetails?.accountNo || "122656705"}</div>
                        </div>
                    </div>

                    <div className="text-right border-r-4 border-slate-900 pr-8">
                        <div className="text-[9px] font-black uppercase tracking-[0.5em] text-slate-200 mb-8 italic">Verified Operator Signature</div>
                        <div className="w-48 h-[2px] bg-slate-900 mb-1"></div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis italic">Digital Certification ID: 99428-X</div>
                    </div>
                </div>
            </div>

            <div className="px-10 pb-6 flex justify-between items-center text-[8px] font-black uppercase tracking-[0.5em] text-slate-300">
                <div>Document Generated via NexrBook Enterprise Industrial Edition</div>
                <div className="bg-slate-900 text-white px-2 py-1 tracking-[0.8em]">INTERNAL RECORD # {Math.floor(Math.random() * 100000)}</div>
            </div>
        </div>
    );
};

export default IndustrialGrid;
